import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from '@/auth/auth-provider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import type { RuntimeConfig } from './runtime-config';
import { RuntimeConfigProvider } from './runtime-config-context';
import { createAppRouter } from './router';
import { CommandPaletteProvider } from './shell/command-palette';
import { ThemeProvider } from './theme/theme-provider';

function RoutedApp({ router }: { router: ReturnType<typeof createAppRouter> }) {
  const auth = useAuth();
  // Re-run route guards whenever the session changes (sign-in, sign-out, expiry).
  useEffect(() => {
    void router.invalidate();
  }, [auth.session, router]);
  return <RouterProvider router={router} context={{ auth }} />;
}

export function AppProviders({
  config,
  queryClient,
  router,
}: {
  config: RuntimeConfig;
  queryClient: QueryClient;
  router?: ReturnType<typeof createAppRouter>;
}) {
  const [appRouter] = useState(() => router ?? createAppRouter(queryClient));
  return (
    <RuntimeConfigProvider value={config}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          {/* Signing out drops every cached response, so nothing leaks into the next session. */}
          <AuthProvider onSignedOut={() => queryClient.clear()}>
            <TooltipProvider delayDuration={300}>
              <CommandPaletteProvider>
                <RoutedApp router={appRouter} />
              </CommandPaletteProvider>
            </TooltipProvider>
            {/* Toasts sit above the floating assistant button (bottom right). */}
            <Toaster richColors closeButton offset={{ bottom: 80 }} mobileOffset={{ bottom: 80 }} />
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </RuntimeConfigProvider>
  );
}
